// Phase 7 D3 — Track C T-C3. ThemeRadioGroup.
//
// Native `<fieldset>` + three `<input type="radio">` controls, one per
// preference (light / dark / follow-system). Submission is implicit — each
// change immediately fires the mutation (Q-2 pessimistic UI shows the
// loader while in flight; the radio itself is disabled while pending).
//
// Accessibility:
//   - `<fieldset>` with `<legend>` provides the group label
//   - each input has a visible `<label>`
//   - `aria-busy` on the fieldset reflects the in-flight mutation
//   - the live region announces success/error after the round-trip

import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import { useUpdatePreferences } from '../../../hooks/usePreferences.js';
import { useTheme, type ThemePreference } from '../../../components/ThemeContext.js';
import { cn } from '../../../lib/cn.js';

interface Option {
  value: ThemePreference;
  label: string;
}

export function ThemeRadioGroup(): JSX.Element {
  const { preference, setPreference } = useTheme();
  const mutation = useUpdatePreferences();
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null);

  const options: readonly Option[] = [
    { value: 'light', label: copy.settings.theme.optionLight },
    { value: 'dark', label: copy.settings.theme.optionDark },
    { value: 'system', label: copy.settings.theme.optionSystem },
  ];

  async function handleChange(next: ThemePreference): Promise<void> {
    setStatusMessage(null);
    // Optimistic-feeling UI: update the local preview immediately so the
    // radio reflects the click. The server is still authoritative — Q-2
    // pessimism means we do not pretend the round-trip succeeded yet.
    setPreference(next);
    try {
      await mutation.mutateAsync({ theme: next });
    } catch {
      setStatusMessage(copy.settings.theme.saveError);
    }
  }

  return (
    <fieldset
      className="flex flex-col gap-3"
      aria-busy={mutation.isPending || undefined}
    >
      <legend className="text-body-strong text-text-primary">
        {copy.settings.theme.radioLegend}
      </legend>
      <div className="flex flex-col gap-2">
        {options.map((opt) => {
          const id = `theme-pref-${opt.value}`;
          const checked = preference === opt.value;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-md border border-border-subtle px-3 py-2 text-body text-text-primary',
                'focus-within:ring-2 focus-within:ring-accent-default focus-within:ring-offset-2 focus-within:ring-offset-surface-base',
                checked && 'border-accent-default bg-accent-subtle',
              )}
            >
              <input
                id={id}
                type="radio"
                name="theme-preference"
                value={opt.value}
                checked={checked}
                disabled={mutation.isPending}
                onChange={() => {
                  void handleChange(opt.value);
                }}
                className="h-4 w-4 cursor-pointer"
              />
              <span>{opt.label}</span>
            </label>
          );
        })}
      </div>
      <p role="status" aria-live="polite" className="text-caption text-state-danger">
        {statusMessage ?? ''}
      </p>
    </fieldset>
  );
}
